import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Monogram, Wordmark } from "./Logo";
import { BRAND } from "../lib/data";
import { Menu, X, Instagram, Mail } from "lucide-react";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/markets", label: "Markets" },
  { to: "/vendors", label: "Vendors" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  // Inner pages have no hero behind the header — force solid style there.
  const onHome = pathname === "/";
  const transparent = onHome && !scrolled;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-gradient-to-b from-[#231F1E]/35 to-transparent border-b border-transparent text-[#EDE6DA]"
          : "backdrop-blur-xl bg-[#EDE6DA]/85 border-b border-[#231F1E]/10 text-[#231F1E]"
      }`}
      style={transparent ? { textShadow: "0 1px 16px rgba(35,31,30,0.45)" } : undefined}
    >
      <div className="px-6 md:px-12 lg:px-16 flex items-center justify-between h-20">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="nav-logo-link"
        >
          <Monogram size={34} color={transparent ? "#EDE6DA" : "#231F1E"} />
          <span className={`font-display text-xl hidden sm:inline ${transparent ? "text-[#EDE6DA]" : "text-[#231F1E]"}`}>
            Workin Chix
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `font-body text-[13px] tracking-[0.18em] uppercase transition-colors ${
                  transparent
                    ? isActive
                      ? "text-[#EDE6DA]"
                      : "text-[#EDE6DA]/80 hover:text-[#EDE6DA]"
                    : isActive
                      ? "text-[#231F1E]"
                      : "text-[#231F1E]/60 hover:text-[#231F1E]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href="https://forms.gle/k5X22jtCcHQh2fZi6"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-apply-cta"
            className={`hidden md:inline-flex btn-primary !py-2.5 !px-5 !text-[11px] ${
              transparent ? "!bg-[#EDE6DA] !text-[#231F1E] !border-[#EDE6DA] hover:!bg-[#D6B66B] hover:!border-[#D6B66B]" : ""
            }`}
            style={transparent ? { textShadow: "none" } : undefined}
          >
            Apply
          </a>
          <button
            data-testid="nav-mobile-toggle"
            className={`lg:hidden ${transparent ? "text-[#EDE6DA]" : "text-[#231F1E]"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-[#EDE6DA] border-t border-[#231F1E]/10"
          data-testid="mobile-nav"
        >
          <nav className="flex flex-col p-6 gap-4">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-display text-2xl ${
                    isActive ? "text-[#231F1E]" : "text-[#9A7456]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href="https://forms.gle/k5X22jtCcHQh2fZi6"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-4 self-start"
              data-testid="mobile-apply-cta"
            >
              Apply as a Vendor
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer
    className="bg-[#231F1E] text-[#EDE6DA] mt-32"
    data-testid="site-footer"
  >
    <div className="px-6 md:px-12 lg:px-16 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <Wordmark color="#EDE6DA" className="!items-start" />
        <p className="font-body text-sm leading-relaxed text-[#EDE6DA]/70 mt-8 max-w-sm">
          Curated pop-up markets and community shopping experiences across the
          Bay Area. Built for makers, made for the neighborhood.
        </p>
      </div>

      <div className="md:col-span-3">
        <h4 className="tracking-overline text-xs text-[#EDE6DA]/60 mb-5">
          Explore
        </h4>
        <ul className="space-y-3">
          {NAV.map((n) => (
            <li key={n.to}>
              <Link
                to={n.to}
                className="font-body text-sm text-[#EDE6DA]/90 hover:text-[#D6B66B] transition-colors"
                data-testid={`footer-link-${n.label.toLowerCase()}`}
              >
                {n.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="md:col-span-4">
        <h4 className="tracking-overline text-xs text-[#EDE6DA]/60 mb-5">
          Stay close
        </h4>
        <div className="flex items-center gap-4">
          <a
            href={BRAND.socials.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="w-10 h-10 rounded-full border border-[#EDE6DA]/30 flex items-center justify-center hover:bg-[#D6B66B] hover:text-[#231F1E] hover:border-[#D6B66B] transition-all"
            data-testid="footer-instagram"
            aria-label="Instagram"
          >
            <Instagram size={16} />
          </a>
          <a
            href={BRAND.socials.email}
            className="w-10 h-10 rounded-full border border-[#EDE6DA]/30 flex items-center justify-center hover:bg-[#D6B66B] hover:text-[#231F1E] hover:border-[#D6B66B] transition-all"
            data-testid="footer-email"
            aria-label="Email"
          >
            <Mail size={16} />
          </a>
        </div>
        <p className="font-body text-xs text-[#EDE6DA]/50 mt-10 leading-relaxed">
          © {new Date().getFullYear()} {BRAND.name}. Bay Area, California.
          <br />All rights reserved.
        </p>
      </div>
    </div>
  </footer>
);

const Layout = () => (
  <div className="min-h-screen bg-[#EDE6DA] text-[#231F1E]">
    <Header />
    <main className="pt-20">
      <Outlet />
    </main>
    <Footer />
  </div>
);

export default Layout;
